import { useState, useRef, useCallback } from 'react'
import { uploadVideoDirect } from '@/services/videoUploadService'
import type { Agent } from '@atproto/api'
import type { VideoBlobRef } from '@/services/videoUploadService'

export type VideoUploadPhase = 'idle' | 'uploading' | 'processing' | 'done' | 'error'

export interface VideoUploadState {
    phase: VideoUploadPhase
    progress: number
    blobRef: VideoBlobRef | null
    error: string | null
}

export interface UseVideoUploadReturn extends VideoUploadState {
    start: (file: File, agent: Agent) => void
    cancel: () => void
    reset: () => void
}

function toFriendlyError(message: string): string {
    if (/daily|limit.*reach|reach.*limit/i.test(message)) {
        return 'Daily video upload limit reached — try again tomorrow'
    }
    if (/network error|Failed to fetch|XHR/i.test(message)) {
        return 'Upload failed — check your connection and try again'
    }
    const httpMatch = message.match(/HTTP (\d{3})/)
    if (httpMatch) {
        return `Upload failed (HTTP ${httpMatch[1]})`
    }
    if (/timed out/i.test(message)) {
        return 'Processing timed out — please try again'
    }
    if (/processing failed|JOB_STATE_FAIL/i.test(message)) {
        return 'Video processing failed — try a different file'
    }
    return message || 'Upload failed — please try again'
}

export function useVideoUpload(): UseVideoUploadReturn {
    const [phase, setPhase] = useState<VideoUploadPhase>('idle')
    const [progress, setProgress] = useState(0)
    const [blobRef, setBlobRef] = useState<VideoBlobRef | null>(null)
    const [error, setError] = useState<string | null>(null)
    const abortControllerRef = useRef<AbortController | null>(null)

    const cancel = useCallback(() => {
        abortControllerRef.current?.abort()
        abortControllerRef.current = null
        setPhase('idle')
        setProgress(0)
        setBlobRef(null)
        setError(null)
    }, [])

    const reset = useCallback(() => {
        abortControllerRef.current?.abort()
        abortControllerRef.current = null
        setPhase('idle')
        setProgress(0)
        setBlobRef(null)
        setError(null)
    }, [])

    const start = useCallback((file: File, agent: Agent) => {
        // Cancel any in-progress upload before starting a new one
        abortControllerRef.current?.abort()

        const controller = new AbortController()
        abortControllerRef.current = controller

        setPhase('uploading')
        setProgress(0)
        setBlobRef(null)
        setError(null)

        uploadVideoDirect(agent, file, {
            signal: controller.signal,
            onUploadProgress: (pct: number) => {
                setProgress(pct)
            },
            onProcessingProgress: (pct: number) => {
                setPhase('processing')
                setProgress(pct)
            },
        })
            .then((blob: VideoBlobRef) => {
                if (controller.signal.aborted) return
                setBlobRef(blob)
                setPhase('done')
            })
            .catch((err: unknown) => {
                if (err instanceof DOMException && err.name === 'AbortError') {
                    // Guard: only reset state if this upload is still the active one.
                    // cancel() nulls abortControllerRef.current, so this check is false
                    // when cancel() already reset state synchronously — avoiding a
                    // double-reset. It is also false when start() was called again before
                    // this AbortError resolved, since the new controller owns the ref.
                    if (abortControllerRef.current === controller) {
                        setPhase('idle')
                        setProgress(0)
                        setBlobRef(null)
                        setError(null)
                    }
                    return
                }
                // Don't overwrite state from a newer upload that has superseded this one
                if (abortControllerRef.current !== controller) return
                const message = err instanceof Error ? err.message : String(err)
                setError(toFriendlyError(message))
                setPhase('error')
            })
    }, [])

    return { phase, progress, blobRef, error, start, cancel, reset }
}
